/**
 * Mutaciones local-first de EVALUACIÓN (F3-B).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs`), que aplica la authz:
 *   • assignment (contenido): autor/editor del curso.
 *   • assignment_submission: alumno matriculado ACTIVO sobre su propia fila
 *     (user_id forzado al actor; `late` sellado por el servidor).
 *   • assignment_grade / grade_category / grade_item: manager de la cohorte
 *     (org/campus_admin o profesor lead).
 *
 * Las notas también pueden aplicarse en lote por REST (`/cohorts/:id/grades/bulk`)
 * y la nota final se computa server-authoritative al cerrar la cohorte.
 */

import {
  and,
  assignment,
  assignment_grade,
  assignment_submission,
  eq,
  grade_category,
  grade_item,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";
import type { AssignmentKind, Rubric, SubmissionType } from "../modules/assignment-plugin";
import { addCourseModuleMutation } from "../modules/mutations";

// ─── Tareas (contenido del curso) ─────────────────────────────────────────────

export interface CreateAssignmentInput {
  courseId: string;
  organizationId?: string | null;
  title?: string;
  instructions?: string | null;
  kind?: AssignmentKind;
  maxScore?: number;
  rubric?: Rubric | null;
  submissionType?: SubmissionType;
  groupMode?: boolean;
}

export async function createAssignmentMutation(
  db: PowerSyncDrizzleDB,
  input: CreateAssignmentInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(assignment).values({
    id,
    course_id: input.courseId,
    organization_id: input.organizationId ?? null,
    title: input.title?.trim() || "Nueva tarea",
    instructions: input.instructions ?? null,
    kind: input.kind ?? "homework",
    max_score: input.maxScore ?? 100,
    rubric: input.rubric ? JSON.stringify(input.rubric) : null,
    submission_type: input.submissionType ?? "text",
    group_mode: input.groupMode ? 1 : 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateAssignmentMutation(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  updates: Partial<{
    title: string;
    instructions: string | null;
    kind: AssignmentKind;
    max_score: number;
    rubric: Rubric | null;
    submission_type: SubmissionType;
    group_mode: boolean;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.instructions !== undefined) {
    patch.instructions = updates.instructions;
  }
  if (updates.kind !== undefined) {
    patch.kind = updates.kind;
  }
  if (updates.max_score !== undefined) {
    patch.max_score = updates.max_score;
  }
  if (updates.rubric !== undefined) {
    patch.rubric = updates.rubric ? JSON.stringify(updates.rubric) : null;
  }
  if (updates.submission_type !== undefined) {
    patch.submission_type = updates.submission_type;
  }
  if (updates.group_mode !== undefined) {
    patch.group_mode = updates.group_mode ? 1 : 0;
  }
  await db.update(assignment).set(patch).where(eq(assignment.id, assignmentId));
}

export async function deleteAssignmentMutation(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
): Promise<void> {
  await db
    .update(assignment)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(assignment.id, assignmentId));
}

/**
 * COMPOSITE (authoring): crea la tarea Y su fila `course_module`
 * (kind='assignment', ref_id=assignment.id) — igual que addResourceModuleMutation.
 */
export async function addAssignmentModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    organizationId?: string | null;
    title?: string;
    position?: number;
  },
): Promise<{ assignmentId: string; moduleId: string }> {
  const assignmentId = await createAssignmentMutation(db, {
    courseId: params.courseId,
    organizationId: params.organizationId,
    title: params.title,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "assignment",
    refId: assignmentId,
    position: params.position ?? 0,
  });
  return { assignmentId, moduleId };
}

// ─── Entrega del alumno ────────────────────────────────────────────────────────

/**
 * El ALUMNO entrega (o actualiza) su tarea local-first. `late` lo sella el
 * servidor; aquí solo body/files. Upsert manual por (assignment,cohort,user)
 * (PowerSync no admite UPSERT sobre vistas).
 */
export async function submitAssignmentMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    body?: string | null;
    files?: string[];
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: assignment_submission.id })
    .from(assignment_submission)
    .where(
      and(
        eq(assignment_submission.assignment_id, params.assignmentId),
        eq(assignment_submission.cohort_id, params.cohortId),
        eq(assignment_submission.user_id, params.userId),
      ),
    );
  const values = {
    body: params.body ?? null,
    files: JSON.stringify(params.files ?? []),
    submitted_at: now,
    updated_at: now,
  };
  if (rows[0]) {
    await db
      .update(assignment_submission)
      .set(values)
      .where(eq(assignment_submission.id, rows[0].id));
    return rows[0].id;
  }
  const id = generateUUID();
  await db.insert(assignment_submission).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    late: 0,
    created_at: now,
    ...values,
  });
  return id;
}

// ─── Calificación (manager; local-first) ───────────────────────────────────────

export async function upsertAssignmentGradeMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    submissionId?: string | null;
    score?: number | null;
    rubricScores?: Record<string, number> | null;
    feedback?: string | null;
    status?: "pending" | "graded" | "excused" | "returned";
  },
): Promise<string> {
  const now = nowISO();
  const rows = await db
    .select({ id: assignment_grade.id })
    .from(assignment_grade)
    .where(
      and(
        eq(assignment_grade.assignment_id, params.assignmentId),
        eq(assignment_grade.cohort_id, params.cohortId),
        eq(assignment_grade.user_id, params.userId),
      ),
    );
  const values = {
    submission_id: params.submissionId ?? null,
    score: params.score ?? null,
    rubric_scores: params.rubricScores ? JSON.stringify(params.rubricScores) : null,
    feedback: params.feedback ?? null,
    status: params.status ?? "graded",
    graded_at: now,
    updated_at: now,
  };
  if (rows[0]) {
    await db.update(assignment_grade).set(values).where(eq(assignment_grade.id, rows[0].id));
    return rows[0].id;
  }
  const id = generateUUID();
  await db.insert(assignment_grade).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    created_at: now,
    ...values,
  });
  return id;
}

// ─── Gradebook: categorías e ítems (manager) ───────────────────────────────────

export async function createGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    name?: string;
    weight?: number;
    dropLowest?: number;
    position?: number;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(grade_category).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    name: params.name?.trim() || "Nueva categoría",
    weight: params.weight ?? 1,
    drop_lowest: params.dropLowest ?? 0,
    position: params.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  categoryId: string,
  updates: Partial<{ name: string; weight: number; drop_lowest: number; position: number }>,
): Promise<void> {
  await db
    .update(grade_category)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(grade_category.id, categoryId));
}

export async function deleteGradeCategoryMutation(
  db: PowerSyncDrizzleDB,
  categoryId: string,
): Promise<void> {
  await db
    .update(grade_category)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(grade_category.id, categoryId));
}

export async function createGradeItemMutation(
  db: PowerSyncDrizzleDB,
  params: {
    cohortId: string;
    organizationId: string;
    categoryId?: string | null;
    sourceKind?: "assignment" | "auto_blocks" | "attendance" | "manual";
    sourceRef?: string | null;
    name?: string;
    maxScore?: number;
    weight?: number;
    position?: number;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(grade_item).values({
    id,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    category_id: params.categoryId ?? null,
    source_kind: params.sourceKind ?? "manual",
    source_ref: params.sourceRef ?? null,
    name: params.name?.trim() || "Nuevo ítem",
    max_score: params.maxScore ?? 100,
    weight: params.weight ?? 1,
    position: params.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateGradeItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
  updates: Partial<{
    category_id: string | null;
    source_kind: string;
    source_ref: string | null;
    name: string;
    max_score: number;
    weight: number;
    position: number;
  }>,
): Promise<void> {
  await db
    .update(grade_item)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(grade_item.id, itemId));
}

export async function deleteGradeItemMutation(
  db: PowerSyncDrizzleDB,
  itemId: string,
): Promise<void> {
  await db
    .update(grade_item)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(grade_item.id, itemId));
}
